'use client';
import { Box, Checkbox, Divider, FormControlLabel, SxProps } from '@mui/material';
import React, {
    Dispatch,
    FunctionComponent,
    SetStateAction,
    useCallback,
    useEffect,
    useMemo,
    useState,
} from 'react';
import { DynamicRequestBody } from '../../../schemas/DynamicRequestBody';
import { ReturnParameterInFilter } from '../../../schemas/ReturnParameterInFilter';
import { ReturnParameterValueInFilter } from '../../../schemas/ReturnParameterValueInFilter';
import { useResponsive } from '../../../contexts/ResponsiveContext';
import PageContainer from '../tableComponents/PageContainer';
import {
    getDisplayNameForReturnParameterInFilter,
    getDisplayNameForReturnParameterValue,
} from '../../../utils/StringRelated';
import { sortCheckboxParams, sortMfgValues } from './checkBoxSorts';

interface FilterCheckBoxProps {
    label: string;
    checked: boolean;
    disabled?: boolean;
    onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
    sx?: SxProps; // 新增可傳入 sx
    chechBoxSx?: SxProps; // 新增可傳入 sx
}

const FilterCheckBox: FunctionComponent<FilterCheckBoxProps> = ({
    label,
    checked,
    disabled,
    onChange,
    sx,
    chechBoxSx,
}) => {
    return (
        <FormControlLabel
            control={
                <Checkbox
                    checked={checked}
                    onChange={onChange}
                    disabled={disabled}
                    sx={{
                        color: '#E5673E',
                        '&.Mui-checked': { color: '#E5673E' },
                        ...chechBoxSx,
                    }}
                />
            }
            data-testid={`filterCheckBox-${label}`}
            label={label}
            // 將預設 sx 與傳入的 sx 合併
            sx={{ marginRight: '1rem', color: 'black', ...sx }}
        />
    );
};

interface FilterCheckBoxGroupProps {
    setGlobalFilterState: Dispatch<SetStateAction<DynamicRequestBody | undefined>>;
    globalFilterState: DynamicRequestBody | undefined;
    originMFG: ReturnParameterInFilter;
    afterFilterMFG: ReturnParameterInFilter | undefined;
    originCheckBox: ReturnParameterInFilter[];
    afterFilterCheckBox: ReturnParameterInFilter[] | undefined;
    language: string;
    globalFilterStateAfterApply: DynamicRequestBody | undefined;
}
const FilterCheckBoxGroup: FunctionComponent<FilterCheckBoxGroupProps> = ({
    setGlobalFilterState,
    globalFilterState,
    originMFG,
    afterFilterMFG,
    originCheckBox,
    afterFilterCheckBox,
    language,
    globalFilterStateAfterApply,
}) => {
    // MFG區域(只有一個parameter)
    const [mfgOptions, setMfgOptions] = useState<ReturnParameterValueInFilter[]>([]);

    useEffect(() => {
        const allMfgValues = originMFG.parameterValues || [];
        setMfgOptions(allMfgValues);
    }, [originMFG]);

    const filteredMfgOptions = useMemo(() => {
        if (!afterFilterMFG) return mfgOptions;
        const allowedMfgIds = new Set<number>(
            (afterFilterMFG.parameterValues || []).map((v) => v.id)
        );
        return sortMfgValues(mfgOptions.filter((mfg) => allowedMfgIds.has(mfg.id)));
    }, [mfgOptions, afterFilterMFG]);

    const mfgFilterKey = originMFG.parameterId.toString();

    const selectedMfgIds = useMemo(() => {
        const val = globalFilterState?.[mfgFilterKey];
        return Array.isArray(val) ? val : [];
    }, [globalFilterState, mfgFilterKey]);

    const handleMfgSelect = useCallback(
        (selectedId: number) => {
            setGlobalFilterState((prevGlobal) => {
                const current = prevGlobal || ({} as DynamicRequestBody);
                const existingValues = Array.isArray(current[mfgFilterKey])
                    ? (current[mfgFilterKey] as number[])
                    : [];
                const newValues = existingValues.includes(selectedId)
                    ? existingValues.filter((v) => v !== selectedId)
                    : [...existingValues, selectedId];

                const next = {
                    ...current,
                    [mfgFilterKey]: newValues.length > 0 ? newValues : undefined,
                };
                return next as DynamicRequestBody;
            });
        },
        [setGlobalFilterState, mfgFilterKey]
    );

    // CheckBox區域
    const checkBoxGroups = useMemo(() => {
        const filtered = originCheckBox
            .map((param) => {
                const filteredValues = (param.parameterValues || []).filter(
                    (val) => val.value === true
                );
                return { ...param, parameterValues: filteredValues };
            })
            .filter((param) => param.parameterValues && param.parameterValues.length > 0)
            .sort((a, b) => (a.sortIndex ?? Infinity) - (b.sortIndex ?? Infinity));

        return sortCheckboxParams(filtered);
    }, [originCheckBox]);

    const filteredCheckBoxGroups = useMemo(() => {
        if (!afterFilterCheckBox) return checkBoxGroups;

        const allowedParamIds = new Set<string | number>();
        for (const p of afterFilterCheckBox) {
            const hasTrue = (p.parameterValues || []).some((v) => v.value === true);
            if (hasTrue) {
                // If it's the stock parameter, add "stock" string, otherwise add the numeric ID
                allowedParamIds.add(p.parameterName === 'stock' ? 'stock' : p.parameterId);
            }
        }

        return checkBoxGroups.filter((p) =>
            // If current parameter is "stock", check against parameterName
            // otherwise check against parameterId
            p.parameterName === 'stock'
                ? allowedParamIds.has('stock')
                : allowedParamIds.has(p.parameterId)
        );
    }, [checkBoxGroups, afterFilterCheckBox]);

    const getCheckBoxState = useCallback(
        (keyName: string) => {
            return globalFilterState?.[keyName] === true;
        },
        [globalFilterState]
    );

    const getCheckBoxStateAfterApply = useCallback(
        (keyName: string) => {
            return globalFilterStateAfterApply?.[keyName] === true;
        },
        [globalFilterStateAfterApply]
    );

    const getMFGStateAfterApply = useCallback(
        (keyName: string) => {
            return keyName in (globalFilterStateAfterApply || {});
        },
        [globalFilterStateAfterApply]
    );

    const handleCheckBoxChange = useCallback(
        (paramName: string, checked: boolean) => {
            setGlobalFilterState((prevGlobal) => {
                const next = {
                    ...(prevGlobal || {}),
                    [paramName]: checked || undefined,
                };
                return next as DynamicRequestBody;
            });
        },
        [setGlobalFilterState]
    );

    const { isMaxScreen, isScreenXxl, isScreenMd, isScreenSm, isScreenXs, responsiveWidth } =
        useResponsive();

    const shouldWrapWithPageContainer = !(isMaxScreen || isScreenXxl);

    const isMobileSize = isScreenSm || isScreenXs || isScreenMd;

    const checkBoxItems = filteredCheckBoxGroups
        .filter((param) => {
            const paramName = param.customColumn
                ? param.parameterName
                : param.parameterId.toString();
            return !getCheckBoxStateAfterApply(paramName);
        })
        .map((param) => {
            const label = getDisplayNameForReturnParameterInFilter(param, language) || '';
            const paramName = param.customColumn
                ? param.parameterName
                : param.parameterId.toString();
            const isChecked = getCheckBoxState(paramName);
            return (
                <FilterCheckBox
                    key={`chk-${param.parameterId}`}
                    label={label}
                    checked={isChecked}
                    onChange={(e) => handleCheckBoxChange(paramName, e.target.checked)}
                />
            );
        });

    const mfgItems = !getMFGStateAfterApply('2')
        ? filteredMfgOptions.map((mfgVal, index) => {
              const label = getDisplayNameForReturnParameterValue(mfgVal, language) || '';
              const isChecked = selectedMfgIds.includes(mfgVal.id);
              return (
                  <React.Fragment key={`mfg-${mfgVal.id}`}>
                      <FilterCheckBox
                          label={label}
                          checked={isChecked}
                          onChange={() => handleMfgSelect(mfgVal.id)}
                      />
                  </React.Fragment>
              );
          })
        : [];

    const showMiddleDivider = checkBoxItems.length > 0 && mfgItems.length > 0;

    const content = (
        <Box
            sx={{
                width: '100%',
                backgroundColor: '#F2F2F2',
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                flexWrap: 'wrap',
                paddingTop: '0',
                paddingLeft: shouldWrapWithPageContainer ? '0' : '1rem',
            }}
        >
            {checkBoxItems}

            {showMiddleDivider && (
                <Divider
                    orientation="vertical"
                    flexItem
                    sx={{ margin: '0 2rem', borderColor: 'black' }}
                />
            )}

            {mfgItems}
        </Box>
    );

    // 行動裝置版內容：採用垂直排列，每個 FilterCheckBox 傳入 sx 調整寬度，Divider 變成橫向且寬度為 responsiveWidth
    const mobileContent = (
        <Box
            sx={{
                width: '100%',
                backgroundColor: 'white',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                paddingTop: '0.5rem',
                gap: '0.5rem',
                paddingBottom: '1rem',
            }}
        >
            {/* CheckBox 區域 */}
            <Box
                sx={{
                    width: responsiveWidth,
                    display: 'grid',
                    // auto-fill + minmax: 會自動根據容器寬度，生成足夠的欄位數，超出寬度就換行
                    gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))',
                    columnGap: '1rem',
                    rowGap: '0.5rem',
                }}
            >
                {filteredCheckBoxGroups
                    .filter((param) => {
                        const paramName = param.customColumn
                            ? param.parameterName
                            : param.parameterId.toString();
                        return !getCheckBoxStateAfterApply(paramName);
                    })
                    .map((param) => {
                        const label =
                            getDisplayNameForReturnParameterInFilter(param, language) || '';
                        const paramName = param.customColumn
                            ? param.parameterName
                            : param.parameterId.toString();
                        const isChecked = getCheckBoxState(paramName);
                        return (
                            <FilterCheckBox
                                key={`chk-${param.parameterId}`}
                                label={label === 'stock' ? 'In Stock' : label}
                                checked={isChecked}
                                onChange={(e) => handleCheckBoxChange(paramName, e.target.checked)}
                                sx={{
                                    width: 'auto',
                                    margin: '0',
                                    padding: '0',
                                    height: '1.5rem',
                                }}
                                chechBoxSx={{ paddingLeft: '0px' }}
                            />
                        );
                    })}
            </Box>
            {/* 橫向 Divider */}
            <Divider
                orientation="horizontal"
                flexItem
                sx={{
                    margin: '0',
                    borderColor: 'black',
                    width: responsiveWidth,
                }}
            />
            {/* MFG 區域 */}
            <Box
                sx={{
                    width: responsiveWidth,
                    display: 'flex',
                    flexDirection: 'row', // 改為水平方向排列
                    flexWrap: 'wrap', // 若空間不足則換行
                    gap: '0.5rem',
                    justifyContent: 'space-between',
                }}
            >
                {!getMFGStateAfterApply('2') &&
                    filteredMfgOptions.map((mfgVal) => {
                        const label = getDisplayNameForReturnParameterValue(mfgVal, language) || '';
                        const isChecked = selectedMfgIds.includes(mfgVal.id);
                        return (
                            <FilterCheckBox
                                key={`mfg-${mfgVal.id}`}
                                label={label}
                                checked={isChecked}
                                onChange={() => handleMfgSelect(mfgVal.id)}
                                sx={{
                                    width: 'auto',
                                    margin: '0',
                                    padding: '0',
                                    height: '1.5rem',
                                }}
                                chechBoxSx={{ paddingLeft: '0px' }}
                            />
                        );
                    })}
            </Box>
        </Box>
    );

    return shouldWrapWithPageContainer ? (
        <PageContainer
            sx={{ padding: 0, backgroundColor: isMobileSize ? 'white' : '#F2F2F2' }}
            innerSx={{ width: responsiveWidth }}
            containerName={'filter-checkbox-responsive'}
        >
            {!isMobileSize && content}
            {isMobileSize && mobileContent}
        </PageContainer>
    ) : (
        content
    );
};

export default FilterCheckBoxGroup;
